import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import * as am5 from "@amcharts/amcharts5";
import * as am5map from "@amcharts/amcharts5/map";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5geodata_indonesiaLow from "@amcharts/amcharts5-geodata/indonesiaLow";
import { coverageService } from "../../services/coverage.service";
import WidgetSkeleton from "../widget/WidgetSkeleton";
import Select from "react-select";

interface Branch {
  id: string;
  name: string;
}

interface GroupedBranches {
  province: string;
  branches: Branch[];
}

const groupedBranches: GroupedBranches[] = [
  { province: "Aceh", branches: [{ id: "BTJ0102000", name: "Banda Aceh" }] },
  { province: "Bali", branches: [{ id: "DPS1710900", name: "Denpasar" }] },
  { province: "Banten", branches: [{ id: "JKT1114500", name: "Jakarta" }] },
  { province: "Bengkulu", branches: [{ id: "BKS0606100", name: "Bengkulu" }] },
  {
    province: "Daerah Istimewa Yogyakarta",
    branches: [{ id: "JOG1547500", name: "Yogyakarta" }],
  },
  {
    province: "Gorontalo",
    branches: [{ id: "GTO3012400", name: "Gorontalo" }],
  },
  { province: "Jambi", branches: [{ id: "DJB0715100", name: "Jambi" }] },
  {
    province: "Jawa Barat",
    branches: [
      { id: "BDO1302200", name: "Bandung" },
      { id: "CBN1310400", name: "Cirebon" },
    ],
  },
  {
    province: "Jawa Tengah",
    branches: [
      { id: "SRG1438000", name: "Semarang" },
      { id: "SOC1442200", name: "Surakarta (Solo)" },
    ],
  },
  {
    province: "Jawa Timur",
    branches: [
      { id: "MLG1624600", name: "Malang" },
      { id: "SUB1642100", name: "Surabaya" },
    ],
  },
  {
    province: "Kalimantan Barat",
    branches: [{ id: "PNK2434800", name: "Pontianak" }],
  },
  {
    province: "Kalimantan Selatan",
    branches: [{ id: "BDJ2303400", name: "Banjarmasin" }],
  },
  {
    province: "Kalimantan Tengah",
    branches: [{ id: "PKY2231500", name: "Palangkaraya" }],
  },
  {
    province: "Kalimantan Timur",
    branches: [
      { id: "BPN2001900", name: "Balikpapan" },
      { id: "SRI2036900", name: "Samarinda" },
      { id: "BEJ2006500", name: "Berau" },
    ],
  },
  {
    province: "Kalimantan Utara",
    branches: [{ id: "TRK2144300", name: "Tarakan" }],
  },
  {
    province: "Kepulauan Bangka Belitung",
    branches: [{ id: "PGK0832200", name: "Pangkal Pinang" }],
  },
  {
    province: "Kepulauan Riau",
    branches: [
      { id: "BTH0404700", name: "Batam" },
      { id: "TNJ0443800", name: "Tanjung Pinang" },
    ],
  },
  {
    province: "Lampung",
    branches: [{ id: "TKG1002100", name: "Bandar Lampung" }],
  },
  { province: "Maluku", branches: [{ id: "AMQ3201400", name: "Ambon" }] },
  {
    province: "Maluku Utara",
    branches: [{ id: "TTE3145100", name: "Ternate" }],
  },
  {
    province: "Nusa Tenggara Barat",
    branches: [{ id: "AMI1826500", name: "Mataram" }],
  },
  {
    province: "Nusa Tenggara Timur",
    branches: [{ id: "KOE1920500", name: "Kupang" }],
  },
  {
    province: "Papua",
    branches: [
      { id: "DJJ3315200", name: "Jayapura" },
      { id: "TIM3327400", name: "Mimika" },
    ],
  },
  { province: "Papua Barat", branches: [{ id: "SOQ3440300", name: "Sorong" }] },
  { province: "Riau", branches: [{ id: "PKU0333500", name: "Pekanbaru" }] },
  { province: "Sulawesi Barat", branches: [] },
  {
    province: "Sulawesi Selatan",
    branches: [{ id: "UPG2524500", name: "Makassar" }],
  },
  {
    province: "Sulawesi Tengah",
    branches: [{ id: "PLW2831800", name: "Palu" }],
  },
  {
    province: "Sulawesi Tenggara",
    branches: [{ id: "KDI2617500", name: "Kendari" }],
  },
  {
    province: "Sulawesi Utara",
    branches: [{ id: "MDC2925700", name: "Manado" }],
  },
  {
    province: "Sumatera Barat",
    branches: [{ id: "PDG0530700", name: "Padang" }],
  },
  {
    province: "Sumatera Selatan",
    branches: [{ id: "PLM0931600", name: "Palembang" }],
  },
  {
    province: "Sumatera Utara",
    branches: [{ id: "MES0226700", name: "Medan" }],
  },
];

type Props = {
  costOfSales: any;
  isCostOfSalesLoading: boolean;
};

const Section6 = ({ costOfSales, isCostOfSalesLoading }: Props) => {
  const [isOutboundDropdownOpen, setIsOutboundDropdownOpen] = useState(false);
  const [selectedOutboundOption, setSelectedOutboundOption] =
    useState("All Origin");
  const [deliveryCoverage, setDeliveryCoverage] = useState<any>(null);
  const [outboundCoverage, setOutboundCoverage] = useState<any>(null);

  const [isDeliveryDropdownOpen, setIsDeliveryDropdownOpen] = useState(false);
  const [selectedDeliveryOption, setSelectedDeliveryOption] =
    useState("All Origin");
  const [deliveryMapRoot, setDeliveryMapRoot] = useState<am5.Root | null>(null);

  const getBranchId = (branchName: string): string => {
    if (branchName === "All Origin") {
      return "";
    }
    const allBranches = groupedBranches.flatMap((group) => group.branches);
    const foundBranch = allBranches.find(
      (branch) => branch.name === branchName
    );
    return foundBranch ? foundBranch.id : "";
  };

  const handleDeliveryDropdownText = (
    event: React.MouseEvent<HTMLAnchorElement>,
    text: string
  ) => {
    event.preventDefault();
    setSelectedDeliveryOption(text);
    setIsDeliveryDropdownOpen(false);
  };

  const handleOutboundDropdownText = (
    event: React.MouseEvent<HTMLAnchorElement>,
    text: string
  ) => {
    event.preventDefault();
    setSelectedOutboundOption(text);
    setIsOutboundDropdownOpen(false);
  };

  useEffect(() => {
    let root = am5.Root.new("deliveryMap");
    setDeliveryMapRoot(root);

    root.setThemes([am5themes_Animated.new(root)]);

    let chart = root.container.children.push(
      am5map.MapChart.new(root, {
        panX: "rotateX",
        panY: "translateY",
        projection: am5map.geoMercator(),
      })
    );

    let polygonSeries = chart.series.push(
      am5map.MapPolygonSeries.new(root, {
        geoJSON: am5geodata_indonesiaLow,
        fill: am5.color("#000000"),
        stroke: am5.color("#ffffff"),
      })
    );

    polygonSeries.mapPolygons.template.setAll({
      tooltipText: "{name}",
      interactive: true,
      fill: am5.color("#000000"),
      templateField: "polygonSettings",
    });

    const getProvinceFromBranch = (branchName: string) => {
      const found = groupedBranches.find((group) =>
        group.branches.some((branch) => branch.name === branchName)
      );
      return found ? found.province : null;
    };

    const updateSelectedRegion = () => {
      const selectedProvince = getProvinceFromBranch(selectedDeliveryOption);
      const data = am5geodata_indonesiaLow.features.map((feature: any) => {
        return {
          id: feature.properties.id,
          polygonSettings: {
            fill:
              feature.properties.name === selectedProvince
                ? am5.color("#4570FF")
                : am5.color("#000000"),
          },
        };
      });
      polygonSeries.data.setAll(data);
    };

    updateSelectedRegion();

    return () => {
      root.dispose();
    };
  }, [selectedDeliveryOption]);

  useEffect(() => {
    let root = am5.Root.new("indonesiaMap");
    root.setThemes([am5themes_Animated.new(root)]);

    let chart = root.container.children.push(
      am5map.MapChart.new(root, {
        panX: "rotateX",
        panY: "translateY",
        projection: am5map.geoMercator(),
      })
    );

    let polygonSeries = chart.series.push(
      am5map.MapPolygonSeries.new(root, {
        geoJSON: am5geodata_indonesiaLow,
        fill: am5.color("#000000"),
        stroke: am5.color("#ffffff"),
      })
    );

    polygonSeries.mapPolygons.template.setAll({
      tooltipText: "{name}",
      interactive: true,
      fill: am5.color("#000000"),
      templateField: "polygonSettings",
    });

    const getProvinceFromBranch = (branchName: string) => {
      const found = groupedBranches.find((group) =>
        group.branches.some((branch) => branch.name === branchName)
      );
      return found ? found.province : null;
    };

    const updateSelectedRegion = () => {
      const selectedProvince = getProvinceFromBranch(selectedOutboundOption);
      const data = am5geodata_indonesiaLow.features.map((feature: any) => {
        return {
          id: feature.properties.id,
          polygonSettings: {
            fill:
              feature.properties.name === selectedProvince
                ? am5.color("#4570FF")
                : am5.color("#000000"),
          },
        };
      });
      polygonSeries.data.setAll(data);
    };

    updateSelectedRegion();

    return () => {
      root.dispose();
    };
  }, [selectedOutboundOption]);

  useEffect(() => {
    const branch_id = getBranchId(selectedDeliveryOption);
    coverageService
      .getDeliveryCoverage(branch_id)
      .then((data) => setDeliveryCoverage(data))
      .catch((err) => {
        setDeliveryCoverage(null);
      });
  }, [selectedDeliveryOption]);

  useEffect(() => {
    const branch_id = getBranchId(selectedOutboundOption);
    coverageService
      .getOutboundCoverage(branch_id)
      .then((data) => setOutboundCoverage(data))
      .catch(() => setOutboundCoverage(null));
  }, [selectedOutboundOption]);

  const createDonutChartOptions = (percentage: number): ApexOptions => ({
    chart: {
      type: "donut",
      height: 280,
    },
    colors: ["#547b3c", "#d8d8d8"],
    labels: ["CoS", "Sales"],
    dataLabels: {
      enabled: true,
      formatter: function (val: number, opts: any) {
        return (
          Math.round(opts.w.config.series[opts.seriesIndex] / 1000000000) + "B"
        );
      },
      style: {
        fontSize: "25px",
        fontWeight: "bold",
        colors: ["#ffffff", "#000000"],
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "50%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "22px",
              fontFamily: "Arial",
              color: "#000000",
            },
            value: {
              show: true,
              fontSize: "35px",
              fontFamily: "Arial",
              color: "#000000",
              formatter: function (val: any): string {
                return Math.round(Number(val) / 1000000000) + "B";
              },
            },
            total: {
              show: true,
              label: "",
              color: "#000000",
              fontSize: "45px",
              fontWeight: "bold",
              formatter: function () {
                return percentage + "%";
              },
            },
          },
        },
      },
    },
    legend: {
      position: "bottom",
      fontSize: "16px",
      markers: {
        strokeWidth: 0,
        fillColors: ["#547b3c", "#d8d8d8"],
        size: 15,
        shape: "square",
      },
    },
    tooltip: {
      y: {
        formatter: function (val: number, opts: any) {
          return (
            '<span style="color: #000000;">Rp. ' +
            val.toLocaleString("id-ID") +
            "</span>"
          );
        },
      },
      style: {
        fontSize: "12px",
        fontFamily: "Arial, sans-serif",
      },
      theme: "light",
    },
  });

  return (
    <div className="row mb-3">
      {/* Delivery Coverage Section */}
      <div className="mb-2 col-12 col-sm-12 col-md-6 col-lg-3">
        <div
          className="card bg-white p-4"
          style={{ height: 387, maxHeight: 387 }}
        >
          <div className="row mb-2">
            <div className="col-12">
              <div className="flex justify-between">
                <span className="text-black">DELIVERY COVERAGE</span>
              </div>
            </div>
          </div>
          <div className="row mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="dropdown">
                <button
                  className="btn text-dark bg-white fw-bold dropdown-custom p-0"
                  type="button"
                  onClick={() =>
                    setIsDeliveryDropdownOpen(!isDeliveryDropdownOpen)
                  }
                >
                  <div className="d-flex align-items-center">
                    <div className="fw-bold dropdown-text px-3">
                      {selectedDeliveryOption}
                    </div>
                    <div className="dropdown-arrow px-3">
                      <span className="arrow-size">▼</span>
                    </div>
                  </div>
                </button>
                <div
                  className={`dropdown-menu dropdown-menu-custom ${
                    isDeliveryDropdownOpen ? "show" : ""
                  }`}
                >
                  <a
                    key="All Origin"
                    className="dropdown-item"
                    href="#"
                    onClick={(e) => handleDeliveryDropdownText(e, "All Origin")}
                  >
                    All Origin
                  </a>
                  {groupedBranches
                    .flatMap((group) => group.branches)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((branch) => (
                      <a
                        key={branch.id}
                        className="dropdown-item"
                        href="#"
                        onClick={(e) =>
                          handleDeliveryDropdownText(e, branch.name)
                        }
                      >
                        {branch.name}
                      </a>
                    ))}
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-4">
              <div className="row mb-3">
                <div style={{ marginBottom: -5 }}>
                  <span className="fw-bold fs-20 text-black">
                    {deliveryCoverage ? deliveryCoverage.jumlah_kota : "0"}
                  </span>
                </div>
                <div className="text-black fs-12">Wilayah</div>
              </div>
              <div className="row mb-3">
                <div className="text-black fs-12" style={{ marginBottom: -5 }}>
                  COD Area
                </div>
                <div
                  className="me-2 d-flex align-items-center d-flex justify-content-between"
                  style={{ marginBottom: -5 }}
                >
                  <span className="fw-bold fs-20 text-black">
                    {deliveryCoverage ? deliveryCoverage.jumlah_cod : "0"}
                  </span>
                  <span
                    className="px-2 rounded text-white fw-bold"
                    style={{ backgroundColor: "#e86d97" }}
                  >
                    {deliveryCoverage
                      ? `${deliveryCoverage.cod_percentage}%`
                      : "0"}
                  </span>
                </div>
                <div className="text-black fs-12">Wilayah</div>
              </div>
              <div className="row mb-3">
                <div className="text-black fs-12" style={{ marginBottom: -5 }}>
                  POD Area
                </div>
                <div
                  className="me-2 d-flex align-items-center d-flex justify-content-between"
                  style={{ marginBottom: -5 }}
                >
                  <span className="fw-bold fs-20 text-black">
                    {deliveryCoverage ? deliveryCoverage.jumlah_pod : "0"}
                  </span>
                  <span
                    className="px-2 rounded text-white fw-bold"
                    style={{ backgroundColor: "#e86d97" }}
                  >
                    {deliveryCoverage
                      ? `${deliveryCoverage.pod_percentage}%`
                      : "0"}
                  </span>
                </div>
                <div className="text-black fs-12">Wilayah</div>
              </div>
              <div className="row">
                <div className="text-black fs-12" style={{ marginBottom: -5 }}>
                  3PL Area
                </div>
                <div
                  className="me-2 d-flex align-items-center d-flex justify-content-between"
                  style={{ marginBottom: -5 }}
                >
                  <span className="fw-bold fs-20 text-black">
                    {deliveryCoverage ? deliveryCoverage.jumlah_mitra : "0"}
                  </span>
                  <span
                    className="px-2 rounded text-white fw-bold"
                    style={{ backgroundColor: "#e86d97" }}
                  >
                    {deliveryCoverage
                      ? `${deliveryCoverage.mitra_percentage}%`
                      : "0"}
                  </span>
                </div>
                <div className="text-black fs-12">Wilayah</div>
              </div>
            </div>
            <div className="col-8">
              <div
                id="deliveryMap"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Outbound Coverage Section */}
      <div className="mb-2 col-12 col-sm-12 col-md-6 col-lg-4">
        <div
          className="card bg-white p-4"
          style={{ height: 387, maxHeight: 387 }}
        >
          <div className="row mb-2">
            <div className="col-12">
              <div className="flex justify-between">
                <span className="text-black">OUTBOUND COVERAGE</span>
              </div>
            </div>
          </div>
          <div className="row mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="dropdown">
                <button
                  className="btn text-dark bg-white fw-bold dropdown-custom p-0"
                  type="button"
                  onClick={() =>
                    setIsOutboundDropdownOpen(!isOutboundDropdownOpen)
                  }
                >
                  <div className="d-flex align-items-center">
                    <div className="fw-bold dropdown-text px-3">
                      {selectedOutboundOption}
                    </div>
                    <div className="dropdown-arrow px-3">
                      <span className="arrow-size">▼</span>
                    </div>
                  </div>
                </button>
                <div
                  className={`dropdown-menu dropdown-menu-custom ${
                    isOutboundDropdownOpen ? "show" : ""
                  }`}
                >
                  <a
                    key="All Origin"
                    className="dropdown-item"
                    href="#"
                    onClick={(e) => handleOutboundDropdownText(e, "All Origin")}
                  >
                    All Origin
                  </a>
                  {groupedBranches
                    .flatMap((group) => group.branches)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((branch) => (
                      <a
                        key={branch.id}
                        className="dropdown-item"
                        href="#"
                        onClick={(e) =>
                          handleOutboundDropdownText(e, branch.name)
                        }
                      >
                        {branch.name}
                      </a>
                    ))}
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-4">
              <div className="row mb-3">
                <div style={{ marginBottom: -5 }}>
                  <span className="fw-bold fs-20 text-black">
                    {outboundCoverage ? outboundCoverage.jumlah_kota : "0"}
                  </span>
                </div>
                <div className="text-black fs-12">Wilayah</div>
              </div>
              <div className="row mb-3">
                <div className="text-black fs-12" style={{ marginBottom: -5 }}>
                  COD Area
                </div>
                <div
                  className="me-2 d-flex align-items-center d-flex justify-content-between"
                  style={{ marginBottom: -5 }}
                >
                  <span className="fw-bold fs-20 text-black">
                    {outboundCoverage ? outboundCoverage.jumlah_cod : "0"}
                  </span>
                  <span
                    className="px-2 rounded text-white fw-bold"
                    style={{ backgroundColor: "#e86d97" }}
                  >
                    {outboundCoverage
                      ? `${outboundCoverage.cod_percentage}%`
                      : "0"}
                  </span>
                </div>
                <div className="text-black fs-12">Wilayah</div>
              </div>
              <div className="row mb-3">
                <div className="text-black fs-12" style={{ marginBottom: -5 }}>
                  POD Area
                </div>
                <div
                  className="me-2 d-flex align-items-center d-flex justify-content-between"
                  style={{ marginBottom: -5 }}
                >
                  <span className="fw-bold fs-20 text-black">
                    {outboundCoverage ? outboundCoverage.jumlah_pod : "0"}
                  </span>
                  <span
                    className="px-2 rounded text-white fw-bold"
                    style={{ backgroundColor: "#e86d97" }}
                  >
                    {outboundCoverage
                      ? `${outboundCoverage.pod_percentage}%`
                      : "0"}
                  </span>
                </div>
                <div className="text-black fs-12">Wilayah</div>
              </div>
              <div className="row">
                <div className="text-black fs-12" style={{ marginBottom: -5 }}>
                  3PL Area
                </div>
                <div
                  className="me-2 d-flex align-items-center d-flex justify-content-between"
                  style={{ marginBottom: -5 }}
                >
                  <span className="fw-bold fs-20 text-black">
                    {outboundCoverage ? outboundCoverage.jumlah_mitra : "0"}
                  </span>
                  <span
                    className="px-2 rounded text-white fw-bold"
                    style={{ backgroundColor: "#e86d97" }}
                  >
                    {outboundCoverage
                      ? `${outboundCoverage.mitra_percentage}%`
                      : "0"}
                  </span>
                </div>
                <div className="text-black fs-12">Wilayah</div>
              </div>
            </div>
            <div className="col-8">
              <div
                id="indonesiaMap"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Cost of Sales Section */}
      <div className="mb-2 col-12 col-sm-12 col-md-12 col-lg-5">
        <div className="card bg-white p-4">
          {isCostOfSalesLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "white",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WidgetSkeleton />
            </div>
          )}
          <div className="row mb-2">
            <div className="col-12 mb-2">
              <div className="flex justify-between">
                <span className="text-black">COST OF SALES (CoS)</span>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-sm-6 mb-4 mb-sm-0">
              <div className="text-center">
                <span className="text-black">Last Year</span>
              </div>
              <div
                style={{
                  position: "relative",
                  height: "280px",
                  width: "280px",
                  margin: "auto",
                }}
              >
                {!isCostOfSalesLoading && costOfSales && (
                  <ReactApexChart
                    options={createDonutChartOptions(
                      costOfSales.last_year.percentage
                    )}
                    series={[
                      Number(costOfSales.last_year.cos),
                      Number(costOfSales.last_year.sales),
                    ]}
                    type="donut"
                    height={280}
                  />
                )}
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="text-center">
                <span className="text-black">This Year</span>
              </div>
              <div
                style={{
                  position: "relative",
                  height: "280px",
                  width: "280px",
                  margin: "auto",
                }}
              >
                {!isCostOfSalesLoading && costOfSales && (
                  <ReactApexChart
                    options={createDonutChartOptions(
                      costOfSales.this_year.percentage
                    )}
                    series={[
                      Number(costOfSales.this_year.cos),
                      Number(costOfSales.this_year.sales),
                    ]}
                    type="donut"
                    height={280}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Section6;
